/**
 * PermissionGuard Component
 *
 * Conditionally renders children based on user permissions
 *
 * Examples:
 * - <PermissionGuard resource="players" action="add">...</PermissionGuard>
 * - <PermissionGuard resource="games.gameMenu.stats" requiredLevel="aed">...</PermissionGuard>
 * - <PermissionGuard fallback={<div>No access</div>}>...</PermissionGuard>
 */

import React, { ReactNode } from 'react';
import { sessionManager } from '../../config/sessionManager';
import { PermissionLevel } from '../../config/permissions';

interface PermissionGuardProps {
  children: ReactNode;
  resource: string;
  action?: 'view' | 'add' | 'edit' | 'delete';
  requiredLevel?: PermissionLevel;
  fallback?: ReactNode;
  showFallback?: boolean;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  resource,
  action,
  requiredLevel,
  fallback = null,
  showFallback = false,
}) => {
  // Check permission based on action or required level
  let hasAccess = false;

  if (action) {
    hasAccess = sessionManager.canPerformAction(resource, action);
  } else if (requiredLevel) {
    hasAccess = sessionManager.hasPermission(resource, requiredLevel);
  } else {
    // Default to checking VIEW permission
    hasAccess = sessionManager.hasPermission(resource, PermissionLevel.VIEW);
  }

  // If no access and fallback should be shown
  if (!hasAccess && showFallback && fallback) {
    return <>{fallback}</>;
  }

  // If no access and no fallback or fallback shouldn't be shown
  if (!hasAccess) {
    return null;
  }

  // User has access
  return <>{children}</>;
};

/**
 * Hook to check permissions
 */
export const usePermission = (
  resource: string,
  action?: 'view' | 'add' | 'edit' | 'delete',
  requiredLevel?: PermissionLevel,
) => {
  if (action) {
    return sessionManager.canPerformAction(resource, action);
  } else if (requiredLevel) {
    return sessionManager.hasPermission(resource, requiredLevel);
  } else {
    return sessionManager.hasPermission(resource, PermissionLevel.VIEW);
  }
};

/**
 * Hook to get all permission-related utilities
 */
export const usePermissions = () => {
  return {
    hasPermission: (resource: string, requiredLevel: PermissionLevel = PermissionLevel.VIEW) =>
      sessionManager.hasPermission(resource, requiredLevel),
    canPerformAction: (resource: string, action: 'view' | 'add' | 'edit' | 'delete') =>
      sessionManager.canPerformAction(resource, action),
    canAccessDataSharing: () => sessionManager.canAccessDataSharing(),
    canAccessPracticeGames: () => sessionManager.canAccessPracticeGames(),
    getUserPermissions: () => sessionManager.getUserPermissions(),
    getUserCustomPermissions: () => sessionManager.getUserCustomPermissions(),
    isEmployee: () => sessionManager.isEmployee(),
    isHeadCoach: () => sessionManager.isHeadCoach(),
    isCoach: () => sessionManager.isCoach(),
  };
};
